import { SxProps } from '@mui/material'
import { dateRangePickerDayClasses as classes } from './DateRangePickerDay.data'
import { calendarDimensions, dateRangePickerClasses } from '../DateRangePicker.data'

interface DateRangePickerDayStyles {
  button: SxProps
  dayText: SxProps
  dot: SxProps
  labelText: SxProps
}

const elements = {
  text: {
    zIndex: 2,
  },
  dot: {
    zIndex: 2,
  },
  highlightedCircle: {
    zIndex: 1,
    selector: '&::before',
  },
  backgroundSquare: {
    zIndex: 0,
    selector: '&::after',
  },
}

const border = {
  left: {
    rounded: {
      borderTopLeftRadius: '50%',
      borderBottomLeftRadius: '50%',
    },
    square: {
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
    },
    half: {
      left: '50%',
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
    },
  },
  right: {
    rounded: {
      borderTopRightRadius: '50%',
      borderBottomRightRadius: '50%',
    },
    square: {
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
    },
    half: {
      right: '50%',
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
    },
  },
} as const

// Shorthand aliases for pseudo-element selectors
const sq = elements.backgroundSquare.selector // '&::after'
const ci = elements.highlightedCircle.selector // '&::before'

export const dateRangePickerDayStyles: DateRangePickerDayStyles = {
  button: {
    width: calendarDimensions.dayButtonSize,
    height: calendarDimensions.dayButtonSize,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'text.secondary700',
    position: 'relative',

    '&:focus-visible': {
      outline: '2px solid',
      outlineColor: 'brand.primary600',
      outlineOffset: 2,
      borderRadius: '50%',
    },

    // Circle rendered on selection and hover
    [ci]: {
      position: 'absolute',
      content: '""',
      width: calendarDimensions.dayButtonSize,
      height: calendarDimensions.dayButtonSize,
      zIndex: elements.highlightedCircle.zIndex,
      borderRadius: '50%',
      borderWidth: 1,
      borderStyle: 'solid',
      borderColor: 'transparent',
    },

    // Square rendered for range highlight
    [sq]: {
      position: 'absolute',
      content: '""',
      width: calendarDimensions.dayButtonSize,
      height: calendarDimensions.dayButtonSize,
      zIndex: elements.backgroundSquare.zIndex,
    },

    '&:hover': {
      cursor: 'pointer',
      color: 'text.primaryContrast',
      [ci]: { backgroundColor: 'brand.primary600' },

      /**
       * if hovered element is before the start (user is selecting in reverse end date first and then start date)
       * then background square should have rounded left corners
       */
      [`&.${classes.beforeStart}`]: { [sq]: { ...border.left.rounded } },

      /**
       * if hovered element is after the start (user has selected start date and now selecting end date)
       * then background square should have rounded right corners
       */
      [`&.${classes.afterStart}`]: { [sq]: { ...border.right.rounded } },
    },

    /**
     * if hovered element is before the start (user is selecting in reverse end date first and then start date)
     * then background square should have rounded left corners
     * Above one was while hovering, this is for when not and already selected
     */
    [`&.${classes.startDateSelectedAndAfterHover}:not(.${classes.hasValue})`]: {
      [sq]: { ...border.right.rounded },
      [`&:not(:hover)`]: { [sq]: { ...border.left.square } },
    },

    [`&.${classes.highlighted}`]: {
      [sq]: { backgroundColor: 'brand.primary100' },
      // highlighted text has black color except when it's also start or end date (because they are in circle with white text)
      [`&:not(:hover, .${classes.startDateSelected}, .${classes.endDateSelected})`]: {
        color: 'text.primary900',
      },
    },

    [`&.${classes.startDateSelected}`]: { [sq]: { ...border.left.rounded } },
    [`&.${classes.endDateSelected}`]: { [sq]: { ...border.right.rounded } },

    // Selected start or end date - white text in colored circle
    [`&.${classes.startDateSelected}, &.${classes.endDateSelected}`]: {
      color: 'text.primaryContrast',
      [ci]: { backgroundColor: 'brand.primary600' },
    },

    // text when disabled or not from current month
    [`&.${classes.disabled}, &.${classes.otherMonth}`]: {
      [`&:not(:hover, .${classes.startDateSelected}, .${classes.endDateSelected})`]: {
        color: 'text.disabled',
      },
    },

    [`.${dateRangePickerClasses.fullWidth} &`]: {
      width: '100%',
      overflow: 'hidden',

      [sq]: { width: '100%' },

      // if it's full width, we want the background square to stretch the full width of the button, but still have the circle for the selected day, so we need to override the border radius for the start and end dates
      [`&.${classes.startDateSelected}`]: {
        [sq]: { ...border.left.half },
      },

      // if it's full width, we want the background square to stretch the full width of the button, but still have the circle for the selected day, so we need to override the border radius for the start and end dates
      [`&.${classes.endDateSelected}`]: {
        [sq]: { ...border.right.half },
      },

      // if it's full width and same date is selected we will hide the background square and just show the circle for that day
      [`&.${classes.startDateSelected}.${classes.endDateSelected}`]: {
        [sq]: { display: 'none' },
      },

      // Start selected, user hovering before it (reversing selection) — square at right half, no radius
      [`&.${classes.startDateSelectedAndAfterHover}.${classes.startDateSelected}:not(.${classes.hasValue}):not(:hover)`]:
        {
          [sq]: { right: '50%', left: 'auto', ...border.right.square, ...border.left.square },
        },

      '&:hover': {
        /**
         * if hovered element is before the start (user is selecting in reverse end date first and then start date)
         * then background square should have rounded left corners
         */
        [`&.${classes.beforeStart}`]: {
          [sq]: { ...border.left.half },
        },

        /**
         * if hovered element is after the start (user has selected start date and now selecting end date)
         * then background square should have rounded right corners
         */
        [`&.${classes.afterStart}`]: {
          [sq]: { ...border.right.half },
        },
      },
    },
  },

  dayText: {
    typography: 'textSm',
    zIndex: elements.text.zIndex,
  },

  labelText: {
    fontSize: 6,
    lineHeight: 1,
    color: 'brand.primary600',
    position: 'absolute',
    [`&.${classes.labelTop}`]: { top: 2 },
    [`&.${classes.labelBottom}`]: { bottom: 2 },
    [`.${classes.startDateSelected} &, .${classes.endDateSelected} &`]: {
      color: 'text.primaryContrast',
    },
  },

  dot: {
    position: 'absolute',
    bottom: 4,
    width: 5,
    height: 5,
    borderRadius: '50%',
    bgcolor: 'brand.primary600',
    zIndex: elements.dot.zIndex,
    [`.${classes.startDateSelected} &, .${classes.endDateSelected} &`]: {
      bgcolor: 'text.primaryContrast',
    },
  },
}
